import { useCallback, Suspense, useMemo } from "react";
import {
  Checkbox,
  FormControl,
  ListItemText,
  MenuItem,
  Select,
} from "@mui/material";
import { Skeleton, FormErrorMessage, FormLabel } from "@avenue-8/sidekick-ui";
import { Controller, useFormContext } from "react-hook-form";
import { PropertyTypeTreeDto } from "@shared/apis/property-search/generated";
import { FindListingsFiltersFormSchema } from "@cma/chat-widgets/find-listings-search/helpers/property-search-filters-form-helpers";
import {
  processPropertyTypesForDisplay,
  type ProcessedPropertyType,
  type ProcessedSubType,
} from "@cma/shared/helpers/dynamic-property-types-helpers";

const toKebabCase = (str: string): string => {
  if (!str) return "";
  return str
    .replace(/([a-z])([A-Z])/g, "$1-$2")
    .replace(/[\s_]+/g, "-")
    .toLowerCase();
};

const renderSelectedPropertyTypes = (
  selected: string[] | null,
  processedTypes: ProcessedPropertyType[]
) => {
  if (!selected || selected.length === 0) return "";

  const selectedNames: string[] = [];

  selected.forEach((id) => {
    // Check if it's a parent type
    const parentType = processedTypes.find((pt) => pt.nameId === id);
    if (parentType) {
      selectedNames.push(parentType.name);
      return;
    }

    // Check if it's a subtype
    for (const processedType of processedTypes) {
      const subType = processedType.subTypes.find((st) => st.nameId === id);
      if (subType) {
        selectedNames.push(subType.displayName);
        return;
      }
    }
  });

  return selectedNames.join(", ");
};

interface Props {
  selectedDealType: string;
  propertyTypes: PropertyTypeTreeDto["propertyTypes"];
}

export const DynamicPropertyTypesField = (props: Props) => {
  const { selectedDealType, propertyTypes } = props;
  const {
    control,
    formState: { errors },
  } = useFormContext<FindListingsFiltersFormSchema>();

  const mlsPropertyTypes = propertyTypes.filter(
    (pt) => pt.dealType === selectedDealType
  );

  const processedTypes = useMemo(
    () => processPropertyTypesForDisplay(mlsPropertyTypes),
    [mlsPropertyTypes]
  );

  const handleParentSelected = (
    result: string[],
    parentNameId: string,
    childNameIds: string[]
  ): string[] => {
    result = result.filter((id) => !childNameIds.includes(id));
    if (!result.includes(parentNameId)) {
      result.push(parentNameId);
    }
    childNameIds.forEach((childId) => {
      if (!result.includes(childId)) {
        result.push(childId);
      }
    });
    return result;
  };

  const handleParentDeselected = (
    result: string[],
    parentNameId: string,
    childNameIds: string[]
  ): string[] => {
    return result.filter(
      (id) => id !== parentNameId && !childNameIds.includes(id)
    );
  };

  const handleChildDeselected = (
    result: string[],
    changedId: string,
    parentNameId: string
  ): string[] => {
    return result.filter((id) => id !== changedId && id !== parentNameId);
  };

  const handleChildSelected = (
    result: string[],
    changedId: string,
    parentNameId: string,
    childNameIds: string[]
  ): string[] => {
    result = result.filter((id) => id !== parentNameId);
    if (!result.includes(changedId)) {
      result.push(changedId);
    }
    if (childNameIds.every((id) => result.includes(id))) {
      if (!result.includes(parentNameId)) {
        result.push(parentNameId);
      }
    }
    return result;
  };

  const handlePropertyTypeChange = useCallback(
    (params: {
      newValues: string[];
      currentValues: string[];
      onChange: (value: string[]) => void;
    }) => {
      const { newValues, currentValues, onChange } = params;
      const currentSet = new Set(currentValues);
      const added = newValues.find((id) => !currentSet.has(id));
      const removed = currentValues.find((id) => !newValues.includes(id));
      const changedId = added || removed;

      if (!changedId) {
        onChange(newValues);
        return;
      }

      let result = [...newValues];

      const affectedGroup = processedTypes.find((pt) => {
        if (pt.nameId === changedId) return true;
        if (pt.subTypes.some((st) => st.nameId === changedId)) return true;
        return false;
      });

      if (!affectedGroup) {
        onChange(result);
        return;
      }

      const parentNameId = affectedGroup.nameId;
      const childNameIds = affectedGroup.subTypes.map((st) => st.nameId);

      if (changedId === parentNameId && added === parentNameId) {
        result = handleParentSelected(result, parentNameId, childNameIds);
      } else if (changedId === parentNameId && removed === parentNameId) {
        result = handleParentDeselected(result, parentNameId, childNameIds);
      } else if (childNameIds.includes(changedId) && removed === changedId) {
        result = handleChildDeselected(result, changedId, parentNameId);
      } else if (childNameIds.includes(changedId) && added === changedId) {
        result = handleChildSelected(
          result,
          changedId,
          parentNameId,
          childNameIds
        );
      }

      onChange(result);
    },
    [processedTypes]
  );

  const isSubTypeSelected = (
    subType: ProcessedSubType,
    selected: string[] | undefined
  ): boolean => {
    if (!selected) return false;
    return selected.includes(subType.nameId);
  };

  return (
    <FormControl fullWidth>
      <FormLabel>Property Types</FormLabel>
      <Suspense fallback={<Skeleton component={Select} />}>
        <Controller
          name="propertyTypes"
          control={control}
          render={({ field }) => (
            <Select
              id="propertyTypes"
              label="Property Types"
              multiple
              {...field}
              onChange={(e) => {
                handlePropertyTypeChange({
                  newValues: e.target.value as string[],
                  currentValues: field.value || [],
                  onChange: field.onChange,
                });
              }}
              renderValue={(selected) =>
                renderSelectedPropertyTypes(selected, processedTypes)
              }
              MenuProps={{
                variant: "menu",
                PaperProps: {
                  style: {
                    maxHeight: 280,
                    width: "auto",
                  },
                },
              }}
            >
              {processedTypes.flatMap((processedType, index) => {
                const items = [];
                const isParentSelected = field.value?.includes(
                  processedType.nameId
                );

                if (index > 0) {
                  items.push(
                    <div
                      key={toKebabCase(`spacer-${processedType.nameId}`)}
                      style={{ height: "8px", width: "100%" }}
                    />
                  );
                }

                items.push(
                  <MenuItem
                    key={toKebabCase(processedType.nameId)}
                    value={processedType.nameId}
                  >
                    <Checkbox checked={isParentSelected} />
                    <ListItemText
                      primary={processedType.name}
                      primaryTypographyProps={{ fontWeight: 700 }}
                    />
                  </MenuItem>
                );

                if (processedType.subTypes.length > 0) {
                  items.push(
                    ...processedType.subTypes.map((subType) => {
                      const isChildSelected = isSubTypeSelected(
                        subType,
                        field.value
                      );
                      return (
                        <MenuItem
                          key={toKebabCase(
                            `${processedType.nameId}-${subType.nameId}`
                          )}
                          value={subType.nameId}
                        >
                          <Checkbox checked={isChildSelected} />
                          <ListItemText primary={subType.displayName} />
                        </MenuItem>
                      );
                    })
                  );
                }

                return items;
              })}
            </Select>
          )}
        />
      </Suspense>
      <FormErrorMessage>
        {errors.propertyTypes?.message ?? " "}
      </FormErrorMessage>
    </FormControl>
  );
};
